/**
 * embedConfigBuilder — builds a JSON config describing available SDK data
 * for AI agents that generate custom HTML for WebEmbed/CustomEmbed screens.
 *
 * The config includes:
 *  - Screen metadata (id, name, type)
 *  - Data sources (child sections) with resolved content
 *  - Bound variables per data source + all funnel variables
 *  - SDK method reference with call signatures
 */

import type { LayoutSection } from '@segmently/schema-models/v2';
import type { FunnelVariable } from '@segmently/schema-models/v2';

import { serializeCustomScreenChildSections, resolveCustomScreenBoundVariable } from '../../../../screen-view-v2/customScreenDataSources';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EmbedSdkConfig {
  screen: {
    id: string;
    name?: string;
    type: string;
  };
  dataSources: DataSourceConfig[];
  variables: VariableConfig[];
  sdk: SdkMethodsReference;
}

interface DataSourceConfig {
  label: string;
  kind: string;
  id: string;
  sdkAccess: string;
  items?: DataSourceItem[];
  content?: Record<string, string | undefined>;
  boundVariable?: {
    id: string;
    name: string;
    type: string;
    sdkGet: string;
    sdkSet: string;
  };
}

interface DataSourceItem {
  index: number;
  title?: string;
  subtitle?: string;
  variableValue?: string;
  /** Bound-variable option id for this item (resolved via screenBindings). Write THIS
   *  value with setVariable so edge conditions match — stable, independent of order. */
  optionId?: string;
  productId?: string;
}

interface VariableConfig {
  id: string;
  name: string;
  type: string;
  sdkGet: string;
  sdkSet: string;
  options?: Array<{ id: string; label: string }>;
}

interface SdkMethodsReference {
  dataSources: Record<string, string>;
  variables: Record<string, string>;
  navigation: Record<string, string>;
  products: Record<string, string>;
  userData: Record<string, string>;
  events: Record<string, string>;
}

// ─── Builder ────────────────────────────────────────────────────────────────

export function buildEmbedSdkConfig(params: {
  screenId: string;
  screenName?: string;
  screenType: string;
  childSections: LayoutSection[];
  locale: string;
  variables: Record<string, FunnelVariable>;
}): EmbedSdkConfig {
  const { screenId, screenName, screenType, childSections, locale, variables } = params;

  const dataSources: DataSourceConfig[] = [];

  const serialized = serializeCustomScreenChildSections(childSections, locale, variables, screenId);
  for (const section of childSections) {
    if (!section.id || !section.kind) continue;

    const label = section.label || section.kind;
    const ds: DataSourceConfig = {
      label,
      kind: section.kind,
      id: section.id,
      sdkAccess: buildSdkAccess(section.kind, section.id),
    };

    // Project from the same data payload consumed by the SDK. Prices remain a
    // published-runtime responsibility; authoring must not invent preview prices.
    const data = serialized.find(row => row.id === section.id)?.data;
    switch (section.kind) {
      case 'ProductCatalog':
        ds.items = data?.products?.map(item => ({ index: item.index, title: item.labels.topStart,
          subtitle: item.labels.bottomStart, productId: item.productId || undefined }));
        break;
      case 'OptionsList':
      case 'SingleSelectionList':
      case 'MultipleSelectionList':
      case 'BulletList':
        ds.items = data?.options?.map(item => ({ index: item.index, title: item.title, subtitle: item.subtitle,
          variableValue: item.variableValue, optionId: item.optionId }));
        break;
      case 'Text':
        if (data) ds.content = { title: data.title, subtitle: data.subtitle, subtitle2: data.subtitle2 };
        break;
      case 'Button':
        ds.content = { buttonText: data?.buttonText, actionKey: `section.${section.id}.button`,
          sdkTrigger: `segmentlySDK.triggerButtonAction(${sdkLiteral(section.id)})` };
        break;
      case 'Media':
        if (data) ds.content = { mediaType: data.mediaType, mediaUrl: data.mediaUrl };
        break;
    }

    // Check if a variable is bound to this section
    const boundVar = resolveCustomScreenBoundVariable(section.id, screenId, variables);
    if (boundVar) {
      ds.boundVariable = {
        id: boundVar.id,
        name: boundVar.name,
        type: boundVar.type,
        sdkGet: `segmentlySDK.getVariable(${sdkLiteral(boundVar.id)})`,
        sdkSet: `segmentlySDK.setVariable(${sdkLiteral(boundVar.id)}, value)`,
      };
    }

    dataSources.push(ds);
  }

  // All funnel variables (not just bound ones)
  const variableConfigs: VariableConfig[] = Object.values(variables).map(v => {
    const vc: VariableConfig = {
      id: v.id,
      name: v.name,
      type: v.type,
      sdkGet: `segmentlySDK.getVariable(${sdkLiteral(v.id)})`,
      sdkSet: `segmentlySDK.setVariable(${sdkLiteral(v.id)}, value)`,
    };
    if (v.options && v.options.length > 0) {
      vc.options = v.options.map(o => ({ id: o.id, label: o.label }));
    }
    return vc;
  });

  return {
    screen: {
      id: screenId,
      name: screenName || undefined,
      type: screenType,
    },
    dataSources,
    variables: variableConfigs,
    sdk: buildSdkReference(),
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function sdkLiteral(value: string): string {
  return "'" + value.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r/g, '\\r').replace(/\n/g, '\\n') + "'";
}

function buildSdkAccess(kind: string, label: string): string {
  switch (kind) {
    case 'Text':
      return `segmentlySDK.getChildSectionText(${sdkLiteral(label)})`;
    case 'Media':
      return `segmentlySDK.getChildSectionMedia(${sdkLiteral(label)})`;
    case 'ProductCatalog':
      return `segmentlySDK.getProducts()`;
    default:
      return `segmentlySDK.getChildSection(${sdkLiteral(label)})`;
  }
}

function buildSdkReference(): SdkMethodsReference {
  return {
    dataSources: {
      getChildSections: 'segmentlySDK.getChildSections() → ChildSectionInit[]',
      getChildSection: "segmentlySDK.getChildSection(idOrLabel) → {id, kind, label, data}",
      getChildSectionText: "segmentlySDK.getChildSectionText(name) → {title, subtitle, subtitle2}",
      getChildSectionMedia: "segmentlySDK.getChildSectionMedia(name) → {url, type}",
    },
    variables: {
      getVariable: "segmentlySDK.getVariable(variableId) → string | string[]",
      setVariable: "segmentlySDK.setVariable(variableId, value)",
      getVariableDefinition: "segmentlySDK.getVariableDefinition(variableId) → {id, name, type, options?}",
      getAllVariableValues: "segmentlySDK.getAllVariableValues() → Record<string, unknown>",
      onVariableChange: "segmentlySDK.onVariableChange(variableIdOrStar, callback) → unsubscribe fn — callback receives {variableId, oldValue, newValue}",
      offVariableChange: "segmentlySDK.offVariableChange(variableIdOrStar, callback) — remove listener",
    },
    navigation: {
      navigateNext: "segmentlySDK.navigateNext(data?) — proceed to next screen",
      navigateBack: "segmentlySDK.navigateBack() — go to previous screen",
      triggerOptionAction: "segmentlySDK.triggerOptionAction(sectionIdOrLabel, optionIdOrIndexOrTarget) — trigger a branch for an OptionsList/SingleSelectionList child data-source item",
      triggerButtonAction: "segmentlySDK.triggerButtonAction(sectionIdOrLabel) — trigger a Button child data-source action",
    },
    products: {
      getProducts: "segmentlySDK.getProducts() → [{productId, index, labels, badge}]",
      getProductPrice: "segmentlySDK.getProductPrice(productId) → {displayPrice, duration, interval, ...}",
      getSelectedProduct: "segmentlySDK.getSelectedProduct() → product | null",
      selectProduct: "segmentlySDK.selectProduct(productIdOrIndex) — select a product",
    },
    userData: {
      getEmail: "segmentlySDK.getEmail() → string | undefined",
      getScreenUserData: "segmentlySDK.getScreenUserData(screenIdOrName) → object | null",
      getCustomData: "segmentlySDK.getCustomData(key) → unknown",
      setCustomData: "segmentlySDK.setCustomData(key, value)",
      getSessionId: "segmentlySDK.getSessionId() → string",
    },
    events: {
      trackEvent: "segmentlySDK.trackEvent(name, params?)",
    },
  };
}
